/**
 * Login page.
 */

import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LoginForm } from "@/features/auth/components";
import { BookOpen } from "lucide-react";

export function LoginPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the redirect path from location state or default to dashboard
  const locationState = location.state as { from?: { pathname: string } } | null;
  const from = locationState?.from?.pathname ?? "/";

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      void navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleSuccess = () => {
    void navigate(from, { replace: true });
  };

  return (
    <div className="flex min-h-screen">
      {/* Left panel - Branding */}
      <div className="relative hidden w-1/2 flex-col justify-end overflow-hidden lg:flex lg:p-12">
        <img
          src="/Écosystème flottant du savoir.png"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-black/10" />
        <div className="text-primary-foreground relative z-10 flex flex-col items-start">
          <BookOpen className="mb-5 h-10 w-10" />
          <h1 className="mb-3 text-4xl font-bold">Lumen</h1>
          <p className="max-w-md text-lg opacity-90">Système Intégré de Gestion de Bibliothèque</p>
          <p className="mt-2 opacity-75">Nouvelle Génération</p>
        </div>
      </div>

      {/* Right panel - Login form */}
      <div className="flex w-full flex-col items-center justify-center p-8 lg:w-1/2">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile logo */}
          <div className="flex flex-col items-center lg:hidden">
            <BookOpen className="text-primary mb-4 h-12 w-12" />
            <h1 className="text-foreground text-2xl font-bold">Lumen</h1>
          </div>

          <div className="space-y-2 text-center">
            <h2 className="text-2xl font-semibold tracking-tight">Connexion</h2>
            <p className="text-muted-foreground text-sm">
              Entrez vos identifiants pour accéder à l'application
            </p>
          </div>

          <LoginForm onSuccess={handleSuccess} />

          <p className="text-muted-foreground text-center text-sm">
            En cas de problème de connexion, contactez votre administrateur.
          </p>
        </div>
      </div>
    </div>
  );
}
